import usePermissions from '@/hooks/permissions/usePermissions'
import { QueryHandlers } from '@/lib/utils/queries'
import {
  extractCustomZodIssues,
  extractZodIssues,
  getFirstZodErrorMessage,
  getTranslatedCustomIssueDescription,
} from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { CreateCrtVersion, CrtVersionComputeStatus, UpdateCrtVersion } from '@repo/backend/crt'
import { MILLIS } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

async function getCrtVersions(crtPeriod: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['versions'].$get({ query: { crtPeriod } }, { init: { signal } })
  return await res.json()
}

async function createCrtVersion(data: CreateCrtVersion) {
  const api = getApi()
  const res = await api.crt['versions'].$post({ json: data })
  return await res.json()
}

async function updateCrtVersion(id: number, data: UpdateCrtVersion) {
  const api = getApi()
  const res = await api.crt['versions'][':id'].$patch({
    param: { id: String(id) },
    json: data,
  })
  return await res.json()
}

async function deleteCrtVersion(id: number) {
  const api = getApi()
  await api.crt['versions'][':id'].$delete({ param: { id: String(id) } })
}

// A business-rule rejection (e.g. a second round 2 in the period) reads as its own wording; anything
// else falls back to the field-level message or the generic failure.
function getVersionErrorDescription(error: unknown, t: TFunction, fallbackKey: string) {
  const [ruleIssue] = extractCustomZodIssues(error)
  const fallbackDescription = getFirstZodErrorMessage(
    extractZodIssues(error),
    t,
    fallbackKey,
    'crt',
  )
  if (!ruleIssue) return fallbackDescription

  return getTranslatedCustomIssueDescription(ruleIssue, t, {
    ns: 'crt',
    keyPrefix: 'crtVersions.errors',
    fallbackDescription,
  })
}

export type CrtVersionListItem = Awaited<ReturnType<typeof getCrtVersions>>[number]

/**
 * The versions of a CRT period. Gated on `crt_versions_view` here, for every caller, so a user without
 * it never sends the (403) list request. For them the query reads as an empty, settled list — the same as
 * a period with no versions — rather than pending forever, so callers need no permission check of their own.
 */
export function useCrtVersions(crtPeriod: string, options?: { enabled?: boolean }) {
  const { canUser } = usePermissions()
  const canViewVersions = canUser('crt_versions_view')
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ['crtVersions', crtPeriod],
    queryFn: async ({ signal }) => {
      const previous = queryClient.getQueryData<CrtVersionListItem[]>(['crtVersions', crtPeriod])
      const versions = await getCrtVersions(crtPeriod, signal)
      invalidateSettledVersionPnl(queryClient, crtPeriod, previous, versions)
      return versions
    },
    enabled: canViewVersions && (options?.enabled ?? true),
    placeholderData: canViewVersions ? undefined : [],
    staleTime: MILLIS.MINUTE * 5,
    gcTime: MILLIS.DAY,
    refetchOnWindowFocus: false,
    // A saved version is computed by the worker after the save returns. Poll while any is still computing so
    // its indicator clears (and it becomes loadable) without a manual refresh; stop once all have settled.
    refetchInterval: (query) =>
      query.state.data?.some((version) => isCrtVersionComputing(version))
        ? CRT_VERSION_COMPUTE_POLL_MS
        : false,
  })
}

const CRT_VERSION_COMPUTE_POLL_MS = MILLIS.SECOND * 5

/**
 * The P&L queries a loaded version feeds are paused while it computes (see `useIsCrtVersionComputing`), and
 * any data they hold describes the previous save. When a version the last fetch saw computing has settled,
 * mark the period's P&L and welcome-card queries stale so they refetch against its fresh precomputed rows. Done here, once
 * per list fetch, rather than in each consumer, so several mounted observers don't each invalidate.
 */
function invalidateSettledVersionPnl(
  queryClient: QueryClient,
  crtPeriod: string,
  previous: CrtVersionListItem[] | undefined,
  next: CrtVersionListItem[],
) {
  const wasComputing = new Set(previous?.filter(isCrtVersionComputing).map((version) => version.id))
  const hasSettled = next.some(
    (version) => wasComputing.has(version.id) && !isCrtVersionComputing(version),
  )
  if (!hasSettled) return
  for (const key of SETTLED_VERSION_PERIOD_ROOTS) {
    queryClient.invalidateQueries({ queryKey: [key, crtPeriod] })
  }
  // The variance keys nest the period inside their side/comparison objects rather than at index 1.
  queryClient.invalidateQueries({
    predicate: (query) => {
      const root = query.queryKey[0]
      return (
        typeof root === 'string' &&
        root.startsWith('varianceAnalysis') &&
        JSON.stringify(query.queryKey).includes(`"${crtPeriod}"`)
      )
    },
  })
}

/** The query roots keyed `[root, crtPeriod, …]` that read a version's precomputed rows. */
const SETTLED_VERSION_PERIOD_ROOTS = [
  'pnl',
  'pnlByCategory',
  'assumptionsWalk',
  'perChannelOverview',
  'perChannelBreakdown',
]

/**
 * Whether `versionId` is a version of `crtPeriod` still being computed by the worker. Reads the shared
 * versions list (same query key as the header selector), so it adds no request.
 */
export function useIsCrtVersionComputing(crtPeriod: string, versionId: number | undefined) {
  const { data: versions } = useCrtVersions(crtPeriod, { enabled: versionId != null })
  const version = versions?.find((candidate) => candidate.id === versionId)
  return !!version && isCrtVersionComputing(version)
}

export function isCrtVersionComputing(version: Pick<CrtVersionListItem, 'computeStatus'>) {
  return version.computeStatus === ('pending' satisfies CrtVersionComputeStatus)
}

export function hasCrtVersionComputeFailed(version: Pick<CrtVersionListItem, 'computeStatus'>) {
  return version.computeStatus === ('failed' satisfies CrtVersionComputeStatus)
}

/**
 * Writes a just-saved version into its period's cached list before the refetch lands. The caller's
 * `onSaved` loads it straight away, and the P&L hooks read its `computeStatus` from this list to hold their
 * queries while it computes; if the list still held the pre-save row, they'd fire a live compute first.
 */
function upsertCachedCrtVersion(queryClient: QueryClient, version: CrtVersionListItem) {
  queryClient.setQueryData<CrtVersionListItem[]>(['crtVersions', version.crtPeriod], (versions) => {
    if (!versions) return versions
    const isKnown = versions.some((candidate) => candidate.id === version.id)
    return isKnown
      ? versions.map((candidate) => (candidate.id === version.id ? version : candidate))
      : [version, ...versions]
  })
}

export function useCreateCrtVersion(handlers?: QueryHandlers) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createCrtVersion'],
    mutationFn: (data: CreateCrtVersion) => createCrtVersion(data),
    onSuccess: (created) => {
      upsertCachedCrtVersion(queryClient, created)
      queryClient.invalidateQueries({ queryKey: ['crtVersions'] })
      handlers?.handleOnSuccess?.()
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('crtVersions.createSuccess'),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      handlers?.handleOnError?.()
      const message = getVersionErrorDescription(error, t, 'crtVersions.createFailed')
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateCrtVersion(handlers?: QueryHandlers) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateCrtVersion'],
    mutationFn: ({ id, data }: { id: number; data: UpdateCrtVersion }) =>
      updateCrtVersion(id, data),
    onSuccess: (updated) => {
      upsertCachedCrtVersion(queryClient, updated)
      queryClient.invalidateQueries({ queryKey: ['crtVersions'] })
      handlers?.handleOnSuccess?.()
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('crtVersions.updateSuccess'),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      handlers?.handleOnError?.()
      const message = getVersionErrorDescription(error, t, 'crtVersions.updateFailed')
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteCrtVersion(handlers?: QueryHandlers) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteCrtVersion'],
    mutationFn: (id: number) => deleteCrtVersion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crtVersions'] })
      handlers?.handleOnSuccess?.()
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('crtVersions.deleteSuccess'),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      handlers?.handleOnError?.()
      const message = getFirstZodErrorMessage(
        extractZodIssues(error),
        t,
        'crtVersions.deleteFailed',
        'crt',
      )
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
